import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';

const { FiX, FiFileText, FiChevronRight, FiCheck, FiPrinter, FiShield, FiArrowLeft, FiEdit3, FiUser, FiHome } = FiIcons;

const DOC_TYPE_ICONS = { email: '✉️', lease: '📋', letter: '📄', 'move-in': '🏠', 'move-out': '📦', inspection: '🔍', receipt: '🧾', other: '📝' };

const DocumentGeneratorModal = ({ isOpen, onClose, templates, properties, leases, tenants, onGenerate, customFieldDefs }) => {
  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [previewContent, setPreviewContent] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setStep(1); setSelectedTemplate(null); setSelectedPropertyId(''); setSelectedTenantId('');
      setPreviewContent(''); setPreviewSubject(''); setIsEditing(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedProperty = properties.find(p => p.id === selectedPropertyId);
  const selectedTenant = tenants.find(t => t.id === selectedTenantId);

  // Auto-detect tenant from property when property changes
  const getAutoTenant = (propId) => {
    const prop = properties.find(p => p.id === propId);
    if (!prop) return '';
    const lease = leases.find(l => l.id === prop.currentLeaseId) || leases.find(l => l.propertyId === propId);
    if (lease?.tenantId) return lease.tenantId;
    if (prop.tenantId) return prop.tenantId;
    return '';
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setStep(2);
  };

  const buildReplacements = () => {
    const prop = selectedProperty || {};
    const tenant = selectedTenant || {};
    const lease = leases.find(l => l.id === prop.currentLeaseId) || leases.find(l => l.propertyId === prop.id && l.tenantId === tenant.id) || leases.find(l => l.propertyId === prop.id) || {};

    const r = {
      '[Tenant Name]': tenant.name || '____________________',
      '[Tenant Email]': tenant.email || '____________________',
      '[Tenant Phone]': tenant.phone || '____________________',
      '[Tenant Employer]': tenant.employer || '____________________',
      '[Tenant Position]': tenant.position || '____________________',
      '[Emergency Contact]': tenant.emergencyContact || '____________________',
      '[Property Name]': prop.name || '____________________',
      '[Property Address]': prop.address || '____________________',
      '[Lock Code]': prop.lockCode || '____________________',
      '[Rent Rate]': prop.rentRate ? '$' + Number(prop.rentRate).toLocaleString() : '____________________',
      '[Property Status]': prop.status || '____________________',
      '[Lease Start Date]': lease.startDate ? format(new Date(lease.startDate), 'MMMM dd, yyyy') : '____________________',
      '[Lease End Date]': lease.endDate ? format(new Date(lease.endDate), 'MMMM dd, yyyy') : '____________________',
      '[Rent Amount]': (lease.rentAmount || prop.rentRate) ? '$' + Number(lease.rentAmount || prop.rentRate).toLocaleString() : '____________________',
      '[Deposit Amount]': lease.depositAmount ? '$' + Number(lease.depositAmount).toLocaleString() : '____________________',
      '[Deposit Status]': lease.depositStatus || '____________________',
      '[Lease Status]': lease.status || '____________________',
      '[Current Date]': format(new Date(), 'MMMM dd, yyyy'),
      '[Current Year]': format(new Date(), 'yyyy'),
      '[Manager Name]': '____________________',
      '[Company Name]': 'PropManagePro',
      '[__________________]': '____________________',
      '[____]': '____',
    };

    // Custom fields from tenant and property records
    (customFieldDefs || []).forEach(cf => {
      const key = `[${cf.name}]`;
      if ((cf.appliesTo === 'tenant' || cf.appliesTo === 'both') && tenant.customFields?.[cf.name]) {
        r[key] = tenant.customFields[cf.name];
      } else if ((cf.appliesTo === 'property' || cf.appliesTo === 'both') && prop.customFields?.[cf.name]) {
        r[key] = prop.customFields[cf.name];
      } else {
        r[key] = '____________________';
      }
    });

    return r;
  };

  const applyReplacements = (text) => {
    if (!text) return '';
    let result = text;
    const r = buildReplacements();
    Object.entries(r).forEach(([key, value]) => {
      result = result.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), String(value));
    });
    return result;
  };

  const handleGenerate = () => {
    const content = applyReplacements(selectedTemplate.content);
    const subject = applyReplacements(selectedTemplate.subject || '');
    setPreviewContent(content);
    setPreviewSubject(subject);
    setStep(3);
  };

  const handleFinalize = () => {
    onGenerate({
      name: `${selectedTemplate.name || selectedTemplate.title} - ${selectedProperty?.name || 'General'}`,
      type: selectedTemplate.category || 'General',
      docType: selectedTemplate.docType || 'other',
      date: format(new Date(), 'yyyy-MM-dd'),
      content: previewContent,
      subject: previewSubject,
      propertyId: selectedPropertyId,
      tenantId: selectedTenantId,
      templateId: selectedTemplate.id,
    });
    onClose();
  };

  const handlePrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>${selectedTemplate?.name || 'Document'}</title><style>body{font-family:Georgia,serif;padding:60px;line-height:1.8;color:#333;max-width:800px;margin:0 auto}</style></head><body>${previewContent.replace(/\n/g, '<br/>')}</body></html>`);
    w.document.close();
    w.print();
  };

  // Count how many fields were populated (not blank lines)
  const populatedCount = Object.values(buildReplacements()).filter(v => v !== '____________________' && v !== '____').length;
  const totalFields = Object.keys(buildReplacements()).length;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-900">Generate Document</h2>
            <div className="flex items-center gap-2 mt-1">
              {[1, 2, 3].map(s => (
                <div key={s} className={`h-1 rounded-full w-8 transition-colors ${step >= s ? 'bg-indigo-600' : 'bg-gray-200'}`} />
              ))}
              <span className="text-[10px] font-semibold text-indigo-600 uppercase ml-1">
                {step === 1 ? 'Template' : step === 2 ? 'Link Records' : 'Review & Edit'}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <SafeIcon icon={FiX} size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Step 1: Select Template */}
          {step === 1 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase mb-3">Select Template</p>
              {(templates || []).length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-400">No templates yet. Create one first.</div>
              ) : (templates || []).map(t => (
                <button key={t.id} onClick={() => handleSelectTemplate(t)}
                  className="w-full p-4 rounded-xl border border-gray-100 hover:border-indigo-300 hover:shadow-md transition-all text-left flex items-center justify-between group">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{DOC_TYPE_ICONS[t.docType] || '📝'}</span>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{t.name || t.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-gray-400">{t.category}</span>
                        {t.docType && <span className="text-[10px] text-indigo-500 capitalize">{t.docType.replace('-', ' ')}</span>}
                      </div>
                    </div>
                  </div>
                  <SafeIcon icon={FiChevronRight} className="text-gray-300 group-hover:text-indigo-500" size={16} />
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Link Property + Tenant */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                <p className="text-xs font-semibold text-indigo-700">
                  Link this document to a property and tenant. Their data will auto-fill all merge fields in the template.
                </p>
              </div>

              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase mb-2">
                  <SafeIcon icon={FiHome} size={12} /> Property
                </label>
                <select value={selectedPropertyId} onChange={e => {
                  setSelectedPropertyId(e.target.value);
                  setSelectedTenantId(getAutoTenant(e.target.value));
                }} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none">
                  <option value="">— None (leave property fields blank) —</option>
                  {properties.map(p => (
                    <option key={p.id} value={p.id}>{p.name} — {p.address} ({p.status})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase mb-2">
                  <SafeIcon icon={FiUser} size={12} /> Tenant
                </label>
                <select value={selectedTenantId} onChange={e => setSelectedTenantId(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none">
                  <option value="">— None (leave tenant fields blank) —</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name} — {t.email} ({t.employer || 'No employer'})</option>
                  ))}
                </select>
                {selectedPropertyId && selectedTenantId && (
                  <p className="text-[10px] text-green-600 font-medium mt-1.5">
                    ✓ {populatedCount} of {totalFields} fields will be auto-filled from linked records
                  </p>
                )}
              </div>

              {/* Quick preview of what will be filled */}
              {(selectedPropertyId || selectedTenantId) && (
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase">Data Preview</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    {selectedTenant && <>
                      <span className="text-gray-400">Tenant:</span><span className="text-gray-800 font-medium">{selectedTenant.name}</span>
                      <span className="text-gray-400">Email:</span><span className="text-gray-800 font-medium">{selectedTenant.email}</span>
                      <span className="text-gray-400">Phone:</span><span className="text-gray-800 font-medium">{selectedTenant.phone || '—'}</span>
                      <span className="text-gray-400">Employer:</span><span className="text-gray-800 font-medium">{selectedTenant.employer || '—'}</span>
                    </>}
                    {selectedProperty && <>
                      <span className="text-gray-400">Property:</span><span className="text-gray-800 font-medium">{selectedProperty.name}</span>
                      <span className="text-gray-400">Address:</span><span className="text-gray-800 font-medium">{selectedProperty.address}</span>
                      <span className="text-gray-400">Rent:</span><span className="text-gray-800 font-medium">${Number(selectedProperty.rentRate || 0).toLocaleString()}</span>
                    </>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Review & Edit */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold text-gray-400 uppercase">
                    {isEditing ? 'Editing Document' : 'Document Preview'}
                  </p>
                  {!isEditing && (
                    <button onClick={() => setIsEditing(true)}
                      className="flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-600 rounded-md text-[10px] font-semibold hover:bg-indigo-100 transition-colors">
                      <SafeIcon icon={FiEdit3} size={10} /> Edit
                    </button>
                  )}
                  {isEditing && (
                    <button onClick={() => setIsEditing(false)}
                      className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-600 rounded-md text-[10px] font-semibold hover:bg-green-100 transition-colors">
                      <SafeIcon icon={FiCheck} size={10} /> Done Editing
                    </button>
                  )}
                </div>
                <button onClick={handlePrint} className="p-2 text-gray-400 hover:text-indigo-600 bg-gray-50 rounded-lg transition-colors" title="Print">
                  <SafeIcon icon={FiPrinter} size={14} />
                </button>
              </div>

              {/* Linked record badges */}
              <div className="flex flex-wrap gap-2">
                {selectedTenant && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-[10px] font-semibold rounded-full">
                    <SafeIcon icon={FiUser} size={10} /> {selectedTenant.name}
                  </span>
                )}
                {selectedProperty && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 text-[10px] font-semibold rounded-full">
                    <SafeIcon icon={FiHome} size={10} /> {selectedProperty.name}
                  </span>
                )}
              </div>

              {previewSubject && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Subject</p>
                  {isEditing ? (
                    <input value={previewSubject} onChange={e => setPreviewSubject(e.target.value)}
                      className="w-full px-2 py-1.5 rounded-md border border-gray-200 text-sm outline-none focus:border-indigo-500" />
                  ) : (
                    <p className="text-sm font-medium text-gray-800">{previewSubject}</p>
                  )}
                </div>
              )}

              <div className={`rounded-xl border ${isEditing ? 'border-indigo-300 bg-white' : 'border-gray-200 bg-gray-50'} p-4`}>
                {isEditing ? (
                  <textarea value={previewContent} onChange={e => setPreviewContent(e.target.value)}
                    rows={16}
                    className="w-full text-sm text-gray-800 leading-relaxed font-mono outline-none resize-none bg-transparent" />
                ) : (
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 min-h-[250px] text-sm text-gray-800 leading-relaxed font-serif whitespace-pre-wrap">
                    {previewContent}
                  </div>
                )}
              </div>

              <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100 flex items-center gap-2">
                <SafeIcon icon={FiShield} className="text-indigo-600 shrink-0" size={14} />
                <p className="text-[10px] text-indigo-700 font-medium">
                  Document will be stored in the Vault. {isEditing ? 'You can edit the content before finalizing.' : 'Click Edit to make changes before saving.'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 flex justify-between shrink-0">
          <button onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors">
            {step > 1 && <SafeIcon icon={FiArrowLeft} size={14} />}
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          {step === 2 && (
            <button onClick={handleGenerate}
              className="px-5 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1.5 text-sm">
              Generate Preview <SafeIcon icon={FiChevronRight} size={14} />
            </button>
          )}
          {step === 3 && (
            <button onClick={handleFinalize}
              className="px-5 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1.5 text-sm">
              <SafeIcon icon={FiCheck} size={14} /> Finalize & Store
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentGeneratorModal;
